import React, { useEffect, useCallback } from 'react';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { useDashboard } from '../../../contexts/DashboardContext';
import { ChartCard, ChartGrid, CustomTooltip, CHART_COLORS, formatCurrency, DonutChartWithLabels } from '../ChartComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DepositsLiquidityTab = () => {
  const { dataCache, setTabData, loading, setTabLoading, errors, setTabError, filters, isTabCached } = useDashboard();
  
  const data = dataCache.deposits;

  const fetchData = useCallback(async () => {
    if (isTabCached('deposits')) return;
    
    setTabLoading('deposits', true);
    setTabError('deposits', null);
    
    try {
      const params = new URLSearchParams();
      if (filters.asOnDate) params.append('as_on_date', filters.asOnDate);
      if (filters.branch) params.append('branch', filters.branch);
      
      const response = await fetch(`${API_URL}/api/dashboard/deposits-liquidity?${params}`);
      if (!response.ok) throw new Error('Failed to fetch data');
      
      const result = await response.json();
      setTabData('deposits', result);
    } catch (err) {
      setTabError('deposits', err.message);
    } finally {
      setTabLoading('deposits', false);
    }
  }, [filters, isTabCached, setTabData, setTabLoading, setTabError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading.deposits) {
    return (
      <ChartGrid>
        {[1, 2, 3, 4].map(i => <ChartCard key={i} title="" loading={true} />)}
      </ChartGrid>
    );
  }

  if (errors.deposits) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600">Error loading data: {errors.deposits}</p>
        <button onClick={fetchData} className="mt-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { charts } = data;

  // Status colors
  const statusColors = {
    'Active': CHART_COLORS.primary,
    'Dormant': CHART_COLORS.warning,
    'Inactive': CHART_COLORS.danger,
  };

  return (
    <ChartGrid>
      {/* Chart 1: CASA vs Term Deposits (Donut) */}
      <ChartCard title="CASA vs Term Deposits" subtitle="Deposit composition">
        <div className="h-64">
          <DonutChartWithLabels 
            data={charts.casa_vs_term} 
            colors={[CHART_COLORS.primary, CHART_COLORS.secondary]}
          />
        </div>
      </ChartCard>

      {/* Chart 2: Deposit Maturity Profile (Bar) */}
      <ChartCard title="Deposit Maturity Profile" subtitle="Maturity buckets by balance">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.maturity_buckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `₹${(v/10000000).toFixed(0)}Cr`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" fill={CHART_COLORS.secondary} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      {/* Chart 3: Account Status Distribution (Donut) */}
      <ChartCard title="Account Status Distribution" subtitle="Active, Dormant, Inactive accounts">
        <div className="h-64">
          <DonutChartWithLabels 
            data={charts.account_status} 
            colorMapping={statusColors}
          />
        </div>
      </ChartCard>

      {/* Chart 4: Top Deposit Products (Horizontal Bar) */}
      <ChartCard title="Top Deposit Products" subtitle="By total balance">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.top_products?.slice(0, 8)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `₹${(v/10000000).toFixed(0)}Cr`} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} stroke="#94a3b8" width={100} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" fill={CHART_COLORS.tertiary} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
    </ChartGrid>
  );
};

export default DepositsLiquidityTab;
